import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command"
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn, toTitleCase } from '@/lib/utils';
import { Button } from './ui/button';

interface ComboBoxProps {
    options: {
        value: string; // Used under the hood
        label: string; // Cosmetic -- what the UI displays. Search is based off label by default.
    }[];
    defaultLabel: string;
    onSelect: (value: string, index: string) => void;
    noun: string;
    className?: string;
    maxLabelLength?: number; // New prop for controlling truncation
}

// Helper function to truncate text
const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
};

const ComboBox = ({ options, defaultLabel = "", onSelect, noun, className, maxLabelLength = 15 }: ComboBoxProps) => {
    const [open, setOpen] = React.useState(false)
    const [label, setLabel] = React.useState(defaultLabel)

    React.useEffect(() => {
        setLabel(defaultLabel)
    }, [defaultLabel])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-[160px] justify-between",
                        className
                    )}
                >
                    <span className="truncate mr-2">
                        {label ? toTitleCase(truncateText(label, maxLabelLength))
                            : `Select ${noun}...`}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 flex-shrink-0" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-0">
                <Command
                    filter={(label, search) => {
                        return label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                    }}
                >
                    <CommandInput placeholder="Search..." />
                    <CommandList>
                        <CommandEmpty>No {noun} found.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={(selectedLabel) => {
                                        setLabel(selectedLabel)
                                        setOpen(false)
                                        onSelect(option.value, option.label)
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4 flex-shrink-0",
                                            label === option.label ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <span className="truncate">{option.label}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
};

export default ComboBox;